import { Modal as AntdModal } from 'antd';
import type { ReactNode } from 'react';

import type { ButtonVariant } from '@/components/common/button';

import { OverlayChrome, OverlayFooter } from '../overlay';

import styles from './modal.module.scss';

export type ModalSize = 'sm' | 'md' | 'lg';

const SIZE_WIDTH: Record<ModalSize, number> = { sm: 464, md: 560, lg: 709 };

export type ModalProps = {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  /** Muted line under the title, inside the cream header band. */
  subtitle?: ReactNode;
  size?: ModalSize;
  width?: number | string;
  /** Vertically center the modal (antd default). Pass false to top-align it. */
  centered?: boolean;
  maskClosable?: boolean;
  closeOnEsc?: boolean;
  loading?: boolean;
  children: ReactNode;
  primaryText?: string;
  onPrimary?: () => void;
  primaryVariant?: ButtonVariant;
  primaryDanger?: boolean;
  /** Disables the primary/submit button (e.g. gate on form validity). */
  primaryDisabled?: boolean;
  /** Optional left-aligned footer node (e.g. a switch-mode link). */
  footerLeft?: ReactNode;
  cancelText?: string;
  onCancel?: () => void;
  /** Disables the footer Cancel button (e.g. a wizard's Previous on step one). */
  cancelDisabled?: boolean;
  /** Internal: used by FormModal to wrap body + footer in a <form>. */
  onSubmit?: React.ComponentProps<'form'>['onSubmit'];
  /** Internal: makes the primary footer button type="submit". */
  primaryIsSubmit?: boolean;
  footerAlign?: 'end' | 'center';
  hideFooter?: boolean;
  /** Suppress the footer Cancel button (e.g. a single-primary alert/blocked state). */
  hideCancel?: boolean;
  /** Removes the body's padding and scrolling so the child owns both (see `OverlayChrome`). */
  flushBody?: boolean;
  /** Custom properties only — e.g. the `--overlay-*-padding` insets `OverlayChrome` reads — for a
   *  frame whose chrome spacing differs from the shared default. Geometry stays the chrome's. */
  className?: string;
};

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  size = 'md',
  width,
  centered = true,
  maskClosable = true,
  closeOnEsc = true,
  loading = false,
  children,
  primaryText,
  onPrimary,
  primaryVariant = 'neutral',
  primaryDanger,
  primaryDisabled,
  footerLeft,
  cancelText = 'Cancel',
  onCancel,
  cancelDisabled,
  onSubmit,
  primaryIsSubmit = false,
  footerAlign = 'end',
  hideFooter = false,
  hideCancel = false,
  flushBody = false,
  className,
}: ModalProps) {
  const footer = hideFooter ? undefined : (
    <OverlayFooter
      primaryText={primaryText}
      onPrimary={onPrimary}
      isSubmit={primaryIsSubmit}
      primaryVariant={primaryVariant}
      primaryDanger={primaryDanger}
      disabled={primaryDisabled}
      footerLeft={footerLeft}
      loading={loading}
      cancelText={hideCancel ? undefined : cancelText}
      onCancel={hideCancel ? undefined : (onCancel ?? onClose)}
      cancelDisabled={cancelDisabled}
      align={footerAlign}
    />
  );

  return (
    <AntdModal
      open={open}
      onCancel={onClose}
      centered={centered}
      footer={null}
      closable={false}
      title={null}
      width={width ?? SIZE_WIDTH[size]}
      mask={{ closable: maskClosable }}
      keyboard={closeOnEsc}
      destroyOnHidden
      className={[styles.modal, className].filter(Boolean).join(' ')}
      classNames={{ container: styles.containerReset }}
    >
      <OverlayChrome
        title={title}
        subtitle={subtitle}
        onClose={onClose}
        footer={footer}
        onSubmit={onSubmit}
        flushBody={flushBody}
      >
        {children}
      </OverlayChrome>
    </AntdModal>
  );
}
